import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Download,
  FileText,
  Building2,
  Calendar,
  PieChart,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  FileSpreadsheet,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value || 0);
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const REPORT_TYPES = [
  { value: "financial_summary", label: "Financial Summary", icon: BarChart3, description: "Income, expenses, loans, investments, properties" },
  { value: "tax", label: "Tax Documents", icon: FileText, description: "W-2 forms, 1099s, withholding summary" },
  { value: "property", label: "Property Portfolio", icon: Building2, description: "Properties, units, tenants, mortgage details" },
];

export default function Reports() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedProperty, setSelectedProperty] = useState("all");
  const [reportType, setReportType] = useState("financial_summary");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [incomeRes, expenseRes, propRes] = await Promise.all([
        api.get("/income"),
        api.get("/expenses"),
        api.get("/properties")
      ]);
      setIncomes(incomeRes.data);
      setExpenses(expenseRes.data);
      setProperties(propRes.data);
    } catch {
      toast.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  const filteredIncomes = incomes.filter(inc => inc.date?.startsWith(selectedYear));

  const filteredExpenses = expenses.filter(exp => {
    const yearMatch = exp.date?.startsWith(selectedYear);
    const propertyMatch = selectedProperty === "all" || 
      (selectedProperty === "personal" && !exp.property_id) ||
      exp.property_id === selectedProperty;
    return yearMatch && propertyMatch;
  });

  const totalIncome = filteredIncomes.reduce((sum, inc) => sum + (inc.amount || 0), 0);
  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
  const netProfit = totalIncome - totalExpenses;
  const profitMargin = totalIncome > 0 ? ((netProfit / totalIncome) * 100).toFixed(1) : 0;

  const monthlyData = MONTHS.map((month, index) => {
    const monthNum = String(index + 1).padStart(2, '0');
    const monthPattern = `${selectedYear}-${monthNum}`;
    const monthIncome = filteredIncomes.filter(inc => inc.date?.startsWith(monthPattern)).reduce((sum, inc) => sum + (inc.amount || 0), 0);
    const monthExpense = filteredExpenses.filter(exp => exp.date?.startsWith(monthPattern)).reduce((sum, exp) => sum + (exp.amount || 0), 0);
    return { month: month.substring(0, 3), income: monthIncome, expense: monthExpense, net: monthIncome - monthExpense };
  });

  const expensesByCategory = filteredExpenses.reduce((acc, exp) => {
    const cat = exp.category || "other";
    acc[cat] = (acc[cat] || 0) + (exp.amount || 0);
    return acc;
  }, {});

  const incomeByCategory = filteredIncomes.reduce((acc, inc) => {
    const cat = inc.category || "other";
    acc[cat] = (acc[cat] || 0) + (inc.amount || 0);
    return acc;
  }, {});

  const expensesByProperty = filteredExpenses.reduce((acc, exp) => {
    const propId = exp.property_id || "personal";
    acc[propId] = (acc[propId] || 0) + (exp.amount || 0);
    return acc;
  }, {});

  const getPropertyName = (propId) => {
    if (propId === "personal") return "Personal Expenses";
    const prop = properties.find(p => p.id === propId);
    return prop ? prop.name : "Unknown Property";
  };

  // Backend PDF export
  const exportPDF = async () => {
    setExporting("pdf");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/reports/export/pdf?year=${selectedYear}&report_type=${reportType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `WealthHub_${reportType}_${selectedYear}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch {
      toast.error("Failed to generate PDF");
    } finally {
      setExporting(null);
    }
  };

  // Backend CSV export
  const exportCSV = async () => {
    setExporting("csv");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/reports/export/csv?year=${selectedYear}&report_type=${reportType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `WealthHub_${reportType}_${selectedYear}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV report downloaded");
    } catch {
      toast.error("Failed to generate CSV");
    } finally {
      setExporting(null);
    }
  };

  const currentMonth = new Date().getMonth();
  const currentMonthData = monthlyData[currentMonth] || { income: 0, expense: 0, net: 0 };
  const prevMonthData = monthlyData[currentMonth - 1] || { income: 0, expense: 0, net: 0 };
  const incomeChange = prevMonthData.income > 0 
    ? ((currentMonthData.income - prevMonthData.income) / prevMonthData.income * 100).toFixed(1) : 0;
  const expenseChange = prevMonthData.expense > 0 
    ? ((currentMonthData.expense - prevMonthData.expense) / prevMonthData.expense * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <Layout title="Financial Reports">
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">Loading report data...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Financial Reports">
      <div data-testid="reports-page" className="space-y-6">
        {/* Header with Filters and Export */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger data-testid="year-select" className="w-32 bg-black/50 border-white/10 rounded-sm">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {[2026, 2025, 2024, 2023].map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-48 bg-black/50 border-white/10 rounded-sm">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all">All Properties</SelectItem>
                <SelectItem value="personal">Personal Only</SelectItem>
                {properties.map(prop => (
                  <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger data-testid="report-type-select" className="w-52 bg-black/50 border-white/10 rounded-sm">
                <FileText className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {REPORT_TYPES.map(rt => (
                  <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-2">
            <Button 
              data-testid="export-csv-btn"
              onClick={exportCSV}
              disabled={!!exporting}
              variant="outline" 
              className="border-white/10 hover:bg-white/5 rounded-sm"
            >
              {exporting === "csv" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
            <Button 
              data-testid="export-pdf-btn"
              onClick={exportPDF}
              disabled={!!exporting}
              className="bg-primary text-black font-bold rounded-sm hover:bg-primary/90"
            >
              {exporting === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </div>
        </div>

        {/* Report Type Info */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {(() => {
                const rt = REPORT_TYPES.find(r => r.value === reportType);
                const Icon = rt?.icon || FileText;
                return (
                  <>
                    <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{rt?.label} Report</p>
                      <p className="text-xs text-zinc-500">{rt?.description}</p>
                    </div>
                  </>
                );
              })()}
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="stat-total-income" className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Total Income</p>
                  <p className="text-2xl font-bold text-emerald-500 font-mono mt-1">{formatCurrency(totalIncome)}</p>
                </div>
                <div className="w-12 h-12 rounded bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
              {Number(incomeChange) !== 0 && (
                <div className={`flex items-center gap-1 mt-2 text-xs ${Number(incomeChange) > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {Number(incomeChange) > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {Math.abs(incomeChange)}% vs last month
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="stat-total-expenses" className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-500 font-mono mt-1">{formatCurrency(totalExpenses)}</p>
                </div>
                <div className="w-12 h-12 rounded bg-red-500/20 flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-red-500" />
                </div>
              </div>
              {Number(expenseChange) !== 0 && (
                <div className={`flex items-center gap-1 mt-2 text-xs ${Number(expenseChange) < 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {Number(expenseChange) > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {Math.abs(expenseChange)}% vs last month
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="stat-net-profit" className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Net Profit/Loss</p>
                  <p className={`text-2xl font-bold font-mono mt-1 ${netProfit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {formatCurrency(netProfit)}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded flex items-center justify-center ${netProfit >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                  <DollarSign className={`w-6 h-6 ${netProfit >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-profit-margin" className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Profit Margin</p>
                  <p className={`text-2xl font-bold font-mono mt-1 ${Number(profitMargin) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {profitMargin}%
                  </p>
                </div>
                <div className={`w-12 h-12 rounded flex items-center justify-center ${Number(profitMargin) >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                  <PieChart className={`w-6 h-6 ${Number(profitMargin) >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Breakdown */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Monthly Breakdown - {selectedYear}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {monthlyData.map((data, index) => {
                const maxValue = Math.max(...monthlyData.map(d => Math.max(d.income, d.expense))) || 1;
                const incomeWidth = (data.income / maxValue) * 100;
                const expenseWidth = (data.expense / maxValue) * 100;
                return (
                  <div key={index} className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-1 text-sm text-zinc-500 font-mono">{data.month}</div>
                    <div className="col-span-8 space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="h-3 bg-emerald-500/80 rounded-r transition-all duration-500" style={{ width: `${incomeWidth}%`, minWidth: data.income > 0 ? '4px' : '0' }} />
                        <span className="text-xs text-emerald-400 font-mono w-20">{formatCurrency(data.income)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-3 bg-red-500/80 rounded-r transition-all duration-500" style={{ width: `${expenseWidth}%`, minWidth: data.expense > 0 ? '4px' : '0' }} />
                        <span className="text-xs text-red-400 font-mono w-20">{formatCurrency(data.expense)}</span>
                      </div>
                    </div>
                    <div className={`col-span-3 text-right font-mono text-sm ${data.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {data.net >= 0 ? '+' : ''}{formatCurrency(data.net)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-emerald-500 rounded" />
                <span className="text-xs text-zinc-400">Income</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded" />
                <span className="text-xs text-zinc-400">Expenses</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Category and Property Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <PieChart className="w-5 h-5 text-red-400" />
                Expenses by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(expensesByCategory).length === 0 ? (
                <p className="text-zinc-500 text-center py-4">No expenses recorded</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(expensesByCategory).sort((a, b) => b[1] - a[1]).map(([category, amount]) => {
                    const percentage = totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0;
                    return (
                      <div key={category} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-300 capitalize">{category.replace(/_/g, ' ')}</span>
                          <span className="font-mono text-red-400">{formatCurrency(amount)}</span>
                        </div>
                        <div className="w-full bg-white/5 rounded-full h-2">
                          <div className="bg-red-500 h-2 rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
                        </div>
                        <p className="text-xs text-zinc-500 text-right">{percentage.toFixed(1)}%</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <PieChart className="w-5 h-5 text-emerald-400" />
                Income by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(incomeByCategory).length === 0 ? (
                <p className="text-zinc-500 text-center py-4">No income recorded</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(incomeByCategory).sort((a, b) => b[1] - a[1]).map(([category, amount]) => {
                    const percentage = totalIncome > 0 ? (amount / totalIncome) * 100 : 0;
                    return (
                      <div key={category} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-300 capitalize">{category.replace(/_/g, ' ')}</span>
                          <span className="font-mono text-emerald-400">{formatCurrency(amount)}</span>
                        </div>
                        <div className="w-full bg-white/5 rounded-full h-2">
                          <div className="bg-emerald-500 h-2 rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
                        </div>
                        <p className="text-xs text-zinc-500 text-right">{percentage.toFixed(1)}%</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Expenses by Property */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Expenses by Property
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(expensesByProperty).length === 0 ? (
              <p className="text-zinc-500 text-center py-4">No expenses recorded</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(expensesByProperty).sort((a, b) => b[1] - a[1]).map(([propId, amount]) => {
                  const percentage = totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0;
                  const isPersonal = propId === "personal";
                  return (
                    <div key={propId} className={`p-4 border rounded-sm ${isPersonal ? 'border-zinc-700 bg-zinc-900/50' : 'border-primary/30 bg-primary/5'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className={`w-4 h-4 ${isPersonal ? 'text-zinc-500' : 'text-primary'}`} />
                        <span className="text-sm font-medium text-white truncate">{getPropertyName(propId)}</span>
                      </div>
                      <p className="text-xl font-bold font-mono text-red-400">{formatCurrency(amount)}</p>
                      <p className="text-xs text-zinc-500 mt-1">{percentage.toFixed(1)}% of total expenses</p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
